import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Download, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

interface RawRow {
  flight_number: string;
  dep_time: string;
  dep_airport: string;
  arr_airport: string;
  arr_time: string;
  day: string;
  notes: string;
}

function normaliseRows(rows: RawRow[]): RawRow[] {
  let lastFlightNumber = '';
  return rows.map(row => {
    const fn = row.flight_number?.trim();
    if (!fn || fn === '' || fn === '-') {
      return { ...row, flight_number: lastFlightNumber };
    }
    lastFlightNumber = fn;
    return row;
  });
}

function parseRowsFromText(text: string): RawRow[] {
  const lines = text.split('\n').filter(l => l.trim());
  const rows: RawRow[] = [];

  for (const line of lines) {
    // Match lines that look like flight data rows
    // Pattern: optional flight number, time, 3-letter codes, time, day info
    const timePattern = /\b(\d{2}:?\d{2})\b/g;
    const cityPattern = /\b([A-Z]{3})\b/g;
    const flightPattern = /\b([A-Z]{2}\d{2,4})\b/;

    const times = [...line.matchAll(timePattern)].map(m => m[1]);
    const cities = [...line.matchAll(cityPattern)].map(m => m[1]);
    const flightMatch = line.match(flightPattern);

    if (times.length >= 2 && cities.length >= 2) {
      // Filter out cities that are part of a flight number
      const flightPrefix = flightMatch ? flightMatch[1].substring(0, 2) : '';
      const filteredCities = cities.filter(c => c !== flightPrefix);

      if (filteredCities.length >= 2) {
        // Try to extract day info (numbers 1-7 or day names at end of line)
        const dayMatch = line.match(/\b([1-7]{1,7})\s*$/);
        const notesMatch = line.match(/(?:daily|except|via|thru|eff|\.{2,})\s*.*/i);

        rows.push({
          flight_number: flightMatch ? flightMatch[1] : '',
          dep_time: times[0].includes(':') ? times[0] : times[0].slice(0, 2) + ':' + times[0].slice(2),
          dep_airport: filteredCities[0],
          arr_airport: filteredCities[1],
          arr_time: times[1].includes(':') ? times[1] : times[1].slice(0, 2) + ':' + times[1].slice(2),
          day: dayMatch ? dayMatch[1] : '',
          notes: notesMatch ? notesMatch[0].trim() : '',
        });
      }
    }
  }

  return rows;
}

export default function PdfNormaliserPage() {
  const { profile, loading } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ rows: RawRow[]; filled: number; fileName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading && (!profile || (profile.role !== 'admin' && profile.role !== 'administrator'))) {
      navigate('/');
    }
  }, [loading, profile, navigate]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessing(true);
    setResult(null);
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let allText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((item: any) => item.str).join(' ');
        allText += pageText + '\n';
      }

      const rawRows = parseRowsFromText(allText);
      const normalised = normaliseRows(rawRows);
      const filled = normalised.filter((r, i) => {
        const original = rawRows[i];
        return r.flight_number !== original.flight_number;
      }).length;

      const baseName = file.name.replace(/\.pdf$/i, '');
      setResult({ rows: normalised, filled, fileName: `${baseName}_normalised.pdf` });
    } catch (err: any) {
      setError(err.message || 'Failed to process PDF');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;

    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape' });

    autoTable(doc, {
      head: [['Flight', 'Dep', 'From', 'To', 'Arr', 'Day', 'Notes']],
      body: result.rows.map(r => [
        r.flight_number,
        r.dep_time,
        r.dep_airport,
        r.arr_airport,
        r.arr_time,
        r.day,
        r.notes,
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { top: 20 },
      didDrawPage: (data: any) => {
        doc.setFontSize(14);
        doc.text('Normalised Flight Schedule', 14, 15);
      },
    });

    doc.save(result.fileName);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-xl">
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Schedule PDF Normaliser
        </h1>
        <p className="text-muted-foreground">
          Upload a flight schedule PDF → get a cleaned version with every flight number filled.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        onChange={handleFile}
        className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:opacity-90 cursor-pointer"
      />

      {processing && (
        <div className="flex items-center gap-2 mt-6 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Processing PDF…</span>
        </div>
      )}

      {error && (
        <p className="mt-6 text-destructive text-sm">❌ {error}</p>
      )}

      {result && (
        <div className="mt-6 space-y-4">
          <p className="text-sm">
            ✅ Normalised PDF ready — <strong>{result.rows.length}</strong> rows processed, <strong>{result.filled}</strong> flight numbers filled in
          </p>
          <Button onClick={handleDownload} size="lg" className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Download Normalised PDF
          </Button>
        </div>
      )}
    </div>
  );
}
